import React, { useEffect, useMemo, useState } from 'react';

import { StyleSheet } from 'react-native';

import { Box, Typography } from 'components/ui';
import { VisuallyHidden } from 'components/ui/visually-hidden';
import { primitive, spacing } from 'styles/constants';
import { fontSizes } from 'styles/constants/fonts';
import { getCountdownValue } from 'utils/dateTime';
import { screenReaderOnly } from 'utils/style/screen-reader-only';

const formatNumber = (n: number) => `${n}`.padStart(2, '0');

interface ICountdownProps {
  endTime: number;
  className?: string;
  onFinish: () => void;
  useClockStyle?: boolean;
}

const ScreenReader = screenReaderOnly;

const Countdown = ({ endTime, onFinish, useClockStyle }: ICountdownProps) => {
  const [nowTime, setNowTime] = useState(() => Date.now());

  const timeLeft = useMemo(() => endTime - nowTime, [endTime, nowTime]);

  const { minutes, seconds, screenReaderSec, screenReaderMins } = useMemo(
    () => getCountdownValue(timeLeft),
    [timeLeft]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setNowTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  if (onFinish && timeLeft <= 0) {
    onFinish();
    return null;
  }

  const formattedMinutes = formatNumber(minutes);
  const formattedSeconds = formatNumber(seconds);
  const formattedScreenReaderMins = formatNumber(screenReaderMins);
  const formattedScreenReaderSec = formatNumber(screenReaderSec);

  const screenOnlyContent = `${formattedScreenReaderMins}:${formattedScreenReaderSec}`;

  const screenOnlyElement = (
    <VisuallyHidden accessibilityLabel="timer">
      <ScreenReader accessibilityLiveRegion="assertive">{screenOnlyContent}</ScreenReader>
    </VisuallyHidden>
  );

  let clockElement;

  if (useClockStyle) {
    clockElement = (
      <Box style={styles.clockWrapper} testID="countdown-clock">
        <Box style={styles.clockValue} testID="minutes">
          <Typography style={styles.clockValueText}>{formattedMinutes}</Typography>
        </Box>
        <Typography style={styles.clockColon}>:</Typography>
        <Box style={styles.clockValue} testID="seconds">
          <Typography style={styles.clockValueText}>{formattedSeconds}</Typography>
        </Box>
      </Box>
    );
  } else {
    clockElement = (
      <Typography>
        {formattedMinutes}:{formattedSeconds}
      </Typography>
    );
  }

  return (
    <>
      {clockElement}
      {screenOnlyElement}
    </>
  );
};

export default Countdown;

const styles = StyleSheet.create({
  clockWrapper: { alignItems: 'center', flexDirection: 'row', justifyContent: 'center' },
  clockValue: {
    alignItems: 'center',
    backgroundColor: primitive.$white,
    borderRadius: primitive.$borderRadius,
    height: spacing.$13_5,
    justifyContent: 'center',
    paddingBottom: spacing.$1,
    width: spacing.$13_5,
  },
  clockValueText: {
    color: primitive.$bbqBrown,
    fontSize: fontSizes['4xl'],
    fontWeight: 'bold',
    lineHeight: fontSizes.xs,
  },
  clockColon: {
    color: primitive.$bbqBrown,
    fontSize: fontSizes['4xl'],
    fontWeight: 'bold',
    height: spacing.$13_5,
    lineHeight: fontSizes.sm,
    paddingHorizontal: spacing.$1,
  },
});
